<template>
  <component
    :is="tag"
    ref="element"
    :contenteditable="contenteditable"
    @input="update"
    @blur="update"
    @paste="onPaste"
    @keypress="onKeypress"
  />
</template>

<script setup lang="ts">
interface Props {
  modelValue?: string
  tag?: string
  contenteditable?: boolean | string
  noHtml?: boolean
  noNl?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  tag: 'div',
  contenteditable: true,
  noHtml: true,
  noNl: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | undefined): void
  (e: 'returned', value: string | undefined): void
}>()

const element = ref<HTMLElement>()

function currentContent () {
  return props.noHtml
    ? element.value?.innerText
    : element.value?.innerHTML
}

function replaceAllStr (str: string, search: string, replacement: string) {
  return str
    .split(search)
    .join(replacement)
}

function updateContent (newcontent: string) {
  if (element.value) {
    if (props.noHtml) {
      element.value.innerText = newcontent
    } else {
      element.value.innerHTML = newcontent
    }
  }
}

function update (event: Event) {
  emit('update:modelValue', currentContent())
}

function onPaste (event: ClipboardEvent) {
  event.preventDefault()
  let text = (event as any).originalEvent?.clipboardData?.getData('text/plain') || event.clipboardData?.getData('text/plain') || ''
  if (props.noNl) {
    text = replaceAllStr(text, '\r\n', ' ')
    text = replaceAllStr(text, '\n', ' ')
    text = replaceAllStr(text, '\r', ' ')
  }

  window.document.execCommand('insertText', false, text)
}

function onKeypress (event: KeyboardEvent) {
  if (event.key == 'Enter' && props.noNl) {
    event.preventDefault()
    emit('returned', currentContent())
  }
}

watch(() => props.modelValue, (newval) => {
  if (newval != currentContent()) {
    updateContent(newval ?? '')
  }
}, { immediate: true })

watch(() => props.noHtml, () => {
  updateContent(props.modelValue ?? '')
}, { immediate: true })

watch(() => props.tag, () => {
  updateContent(props.modelValue ?? '')
}, { immediate: true })

onMounted(() => {
  updateContent(props.modelValue ?? '')
})
</script>
